import React, { useEffect, useRef, useState } from "react";

import { SocialButtons } from "../social";
import { closex, closexMobile, menu, TVGuideIcon } from "../icon";
import { SideNav } from "./sideNav";
import { SearchBar } from "./SearchBar";
import { MENUS_TO_REMOVE } from "../../config";
import cx from "classnames";
import { getMenuSectionListMobileQuery, getMenuSectionListQuery } from "../../graphql/queries/getMenuList";
import { useLockBodyScrollOnValue } from "../../helper/useLockBodyScroll";
import { NavigationLinks, Link, MobileLink, isMobile } from "../../types";
import User from "./User";
import { useQuery } from "@apollo/client";
import { LogoIconName, TVGuide } from "../../config";
import { ACCOUNT_URL, tags } from "../../config";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
const HEADER_HEIGHT = "4.5rem";


interface LoginButtonProps {
  isAuthenticated: boolean;
  isReady: boolean;
  onSessionAction: () => void;
}

let LoginButton = ({
  isAuthenticated,
  isReady,
  onSessionAction,
}: LoginButtonProps) => {
  if (!isReady) {
    return null;
  }

  return (
    <div className="sm:hidden flex justify-center bg-blue-pure text-white text-center">
      <a
        className="font-bold p-4 text-2xl md:text-xl text-white hover:text-navy"
        onClick={onSessionAction}
      >
        {isAuthenticated ? "Log out" : "Log in"}
      </a>
    </div>
  );
};

interface Props {
  SessionClickHandler?: () => void;
  isSignedIn?: boolean;
  name?: string;
  links: NavigationLinks;
  cartURL?: string;
}

let Header = () => {
  const { isAuthenticated, isLoading, user, logout, getAccessTokenSilently } =
    useAuth0();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const hasAttemptedSilentAuth = useRef(false);
  const isSilentAuthInFlight = useRef(false);
  const hasResolvedAuthState = !isLoading && !isCheckingSession;
  let currentUrl = window.location.href;
  // Define titles to exclude when user is logged in


  const location = useLocation();

  const redirectToAccount = () => window.location.assign(ACCOUNT_URL);

  const handleSessionAction = (
    isAuthenticated: boolean,
    logout: ReturnType<typeof useAuth0>["logout"]
  ) => {
    if (isAuthenticated) {
      const targetPage = location.pathname + location.search + location.hash;
      const returnTo = `${window.location.origin}/logout?targetPage=${targetPage}`;
      logout({ logoutParams: { returnTo } });
      return;
    }

    redirectToAccount();
  };

  //for header rendering data coming from this service
  const { data: GlobalmenuSectionList } = useQuery<any>(
    getMenuSectionListQuery,
    {
      variables: {
        usePreview: false,
        tags: tags?.globalHeader,
      },
      fetchPolicy: "cache-and-network",
    }
  );

 //for header mobile rendering data coming from this service
 const { data: getMenuSectionListMobile } = useQuery<any>(
  getMenuSectionListMobileQuery,
  {
    variables: {
      usePreview: false,
      tags: tags?.golbalMobileHeader,
    },
    fetchPolicy: "cache-and-network",
  }
);
  //for Social media icons comming from this service

  const { data: GlobalSocialIcons } = useQuery<any>(getMenuSectionListQuery, {
    variables: {
      usePreview: false,
      tags: tags?.globalFooter,
    },
    fetchPolicy: "cache-and-network",
  });
  interface composePageSubMenuDataProps {
    name: string;
    itemUrl: string;
    itemIcon: string;
    internalName: string;
  }

  interface link {
    itemIcon: string | undefined;
    slug: null;
    itemUrl: string;
    sectionLinks: [composePageSubMenuDataProps];
  }
  const SocialIcons: link[] =
    GlobalSocialIcons?.GlobalMenuList?.[0]?.menuSections;

  const composePageSubMenuDataNew: link[] =
    GlobalmenuSectionList?.GlobalMenuList?.[0]?.menuSections;

  const mobieMenudata =
    getMenuSectionListMobile?.GlobalMenuList?.[0]?.menuSections?.filter(
      (linkVal: { slug: string }) => linkVal?.slug === "main-menu-items-mobile"
    ) ?? [];

  const modifiedMobileMenuData = {
    children: mobieMenudata.length > 0
      ? mobieMenudata[0]?.sectionLinks?.map(
          (linkVal: { itemUrl: any; name: any; sectionName: any; sectionLinks: any[] }) => ({
            link: linkVal.itemUrl,
            title: linkVal.name || linkVal?.sectionName,
            children: linkVal?.sectionLinks?.map(subLinkVal => ({
              link: subLinkVal.itemUrl,
              title: subLinkVal.name || subLinkVal.sectionName,
            })) ?? [],
          })
        ) ?? []
      : [],
  };

  const headerMenuChildren = (composePageSubMenuDataNew ?? [])
    .filter(link => link?.slug !== "sky_logo_hamburger" && link?.slug !== "cart_search_tvguide")
    .flatMap(link => link?.sectionLinks ?? [])
    .filter(sectionLink => sectionLink?.name && sectionLink?.itemUrl)
    .map(sectionLink => ({
      link: sectionLink.itemUrl,
      title: sectionLink.name,
    }));

  const sideNavMenuData = modifiedMobileMenuData.children.length > 0
    ? modifiedMobileMenuData
    : { children: headerMenuChildren };

  
  let [showSidebar, showSideBarSet] = useState(false);
  let close = () => showSideBarSet(false);
  useLockBodyScrollOnValue(showSidebar);

  useEffect(() => {
    let isCancelled = false;

    const syncAuthState = async (forceNetwork = false) => {
      if (isLoading || isAuthenticated || isSilentAuthInFlight.current) {
        if (!isLoading && isAuthenticated && !isCancelled) {
          setIsCheckingSession(false);
        }
        return;
      }

      isSilentAuthInFlight.current = true;
      if (!isCancelled) {
        setIsCheckingSession(true);
      }

      try {
        await getAccessTokenSilently(
          forceNetwork ? { cacheMode: "off" } : undefined
        );
      } catch (error: any) {
        const authError = error?.error;
        if (
          authError !== "login_required" &&
          authError !== "consent_required" &&
          authError !== "interaction_required"
        ) {
          console.error("Failed to restore Auth0 session", error);
        }
      } finally {
        isSilentAuthInFlight.current = false;
        if (!isCancelled) {
          setIsCheckingSession(false);
        }
      }
    };

    if (isLoading) {
      return;
    }

    if (isAuthenticated) {
      setIsCheckingSession(false);
    } else if (!hasAttemptedSilentAuth.current) {
      hasAttemptedSilentAuth.current = true;
      syncAuthState(true);
    } else {
      setIsCheckingSession(false);
    }

    const handleWindowFocus = () => {
      if (!document.hidden) {
        syncAuthState(true);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        syncAuthState(true);
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isCancelled = true;
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  return (
    <>
      {showSidebar && (
      <div className="fixed inset-0 z-1000">
        <div
          className="absolute inset-0 bg-blue-darker bg-opacity-50"
          onClick={close}
        ></div>
        <div className="absolute h-full left-0 w-110 md:w-120 max-w-full bg-white flex flex-col">
          <div className="flex flex-col flex-1 min-h-0">
            <div className="px-3 md:px-6">
              <button
                type="button"
                aria-label="Close menu"
                className="w-10 h-10 md:w-16 md:h-16 flex items-center justify-center text-primary-mid-night-blue bg-transparent border-none p-0"
                onClick={close}
              >
                {isMobile ? closexMobile : closex}
              </button>
            </div>
            
            <p className="md:hidden text-2xl md:text-xl text-black font-bold px-8 pt-4">
              {hasResolvedAuthState
                ? isAuthenticated
                  ? `Kia ora, ${user?.nickname || user?.given_name}`
                  : "Welcome"
                : ""}
            </p>
            <SideNav
              close={close} 
              link={sideNavMenuData}
            />
          </div>

          <div className="w-full mt-auto">
            <div className="flex justify-center py-5">
              <SocialButtons link={SocialIcons} />
            </div>
            <div className="sm:hidden flex justify-center bg-blue-pure text-white text-center">
              <LoginButton
                isAuthenticated={isAuthenticated}
                isReady={hasResolvedAuthState}
                onSessionAction={() =>
                  handleSessionAction(isAuthenticated, logout)
                }
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* New Navbar */}
      <div
        className="bg-white font-sans flex items-center justify-start z-100 px-[20px] 2xl:px-50px sticky top-0 shadow-md"
        style={{ height: HEADER_HEIGHT }}
        id="header"
      >
        <div className="flex min-w-16 pr-8">
          <div
            className="flex items-center gap-6 2lg:gap-0 px-0 w-full"
            style={{ height: HEADER_HEIGHT }}
          >
            {/* Hide hamburger menu from desktop. */}
            <div className="2lg:hidden">
              <button
                type="button"
                aria-label="Open menu"
                aria-expanded={showSidebar}
                className="w-10 h-10 relative text-blue-darker top-3px"
                onClick={() => showSideBarSet(true)}
              >
                {menu}
              </button>
            </div>
            <div className="w-full h-full flex items-center">
              {composePageSubMenuDataNew?.map((link, index) => {
                if (link?.slug === "sky_logo_hamburger") {
                  return (
                    <React.Fragment key={index}>
                      {link?.sectionLinks?.map((sectionLinksData, idx) => {
                        if (sectionLinksData?.name === LogoIconName) {
                          return (
                            <a key={idx} href={sectionLinksData?.itemUrl}>
                              <img
                                src={sectionLinksData?.itemIcon}
                                className="sm:h-10 h-32px m-auto"
                                alt="Sky logo"
                              />
                            </a>
                          );
                        }
                        return null;
                      })}
                    </React.Fragment>
                  );
                }
                // Return null if link?.slug !== "sky_logo_hamburger"
                return null;
              })}
            </div>
          </div>
        </div>
        
        <nav id="header-nav" className="new-navbar font-extrabold font-sans">
  {composePageSubMenuDataNew?.map((link, index: React.Key | null | undefined) => {
    if (
      link?.slug !== "sky_logo_hamburger" &&
      link?.slug !== "cart_search_tvguide"
    ) {
      return (
        <React.Fragment key={index}>
          {link?.sectionLinks
            ?.filter(sectionLink => {
              const name = sectionLink?.name?.trim().toLowerCase();
              const excluded = MENUS_TO_REMOVE.map(title => title?.toLowerCase());
              const shouldExclude = isAuthenticated && name && excluded.includes(name);
              return !shouldExclude;
            })            
            .map((sectionLink, idx: React.Key | null | undefined) => {
              const current = currentUrl.includes(sectionLink.itemUrl ?? "");
              return (
                <a
  key={idx}
  href={sectionLink.itemUrl}
  className={cx(
    "text-blue-darker leading-normal",
    {
      "font-skyBold  md:sky-h6-black": current,
      "font-skyBook font-bold md:sky-h7": !current,
    }
  )}
>
  {sectionLink.name}
</a>
              );
            })}
        </React.Fragment>
      );
    }
    return null;
  })}
</nav>
<div
          className={`flex items-center gap-6 2lg:gap-4 justify-end 2lg:min-w-64 pl-6 ml-auto`}
        >
          {composePageSubMenuDataNew?.map((link, index) => {
            if (link?.slug === "cart_search_tvguide") {
              return (
                <React.Fragment key={index}>
                  {link?.sectionLinks?.map((sectionLinksData, idx) => {
                    if (sectionLinksData?.name === TVGuide) {
                      return (
                        <a
                          key={idx}
                          className="flex"
                          href={sectionLinksData?.itemUrl}
                        >
                          <TVGuideIcon />
                        </a>
                      );
                    }
                    return null;
                  })}
                </React.Fragment>
              );
            }
            return null;
          })}

        {composePageSubMenuDataNew? <SearchBar />:""}
          {composePageSubMenuDataNew &&<div
            className={`${!isAuthenticated ? "border-2 rounded-full  sm:pt-2":"md:pt-0"} sm:flex items-center border-blue-darker px-4 hidden`}
            style={{borderColor:" #0057FF"}}
          >
            {<User  isAuthenticated ={isAuthenticated}/>}
            <div className="flex flex-col " style={{fontFamily:'MarkPro-Book SKY'}}>
              {isAuthenticated && (
                <>
                <p className="text-blue-darker font-normal block whitespace-no-wrap text-xs">
                  Kia Ora, 
                </p>
                <p className="text-blue-darker font-bold block whitespace-no-wrap text-sm">{user?.nickname || user?.given_name}
                  </p>
                </>
              )}
              {hasResolvedAuthState ? (
                <a
                  className={`text-blue-pure sm:pb-2 block whitespace-no-wrap cursor-pointer font-bold ${isAuthenticated?'underline underline-offset-4 text-base text-start':"text-xl"}`}
                  onClick={() => handleSessionAction(isAuthenticated, logout)}
                >
                  {isAuthenticated ? "Log out" : "Log In"}
                </a>
              ) : (
                <span className="text-blue-pure sm:pb-2 block whitespace-no-wrap font-bold text-xl opacity-0">
                  Log In
                </span>
              )}
            </div>
          </div>}
        </div>
      </div>
    </>
  );
};

export { Header, HEADER_HEIGHT };
